import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import GoodsReceiptDetailPage from './pages/goods-receipt/GoodsReceiptDetailPage';
import GoodsReceiptListPage from './pages/goods-receipt/GoodsReceiptListPage';
import GoodsReceiptNewPage from './pages/goods-receipt/GoodsReceiptNewPage';
import HubTransferDetailPage from './pages/hub-transfer/HubTransferDetailPage';
import HubTransferListPage from './pages/hub-transfer/HubTransferListPage';
import HubTransferNewPage from './pages/hub-transfer/HubTransferNewPage';
import InboundRequestDetailPage from './pages/inbound-request/InboundRequestDetailPage';
import InboundRequestListPage from './pages/inbound-request/InboundRequestListPage';
import OutboundRequestDetailPage from './pages/outbound-request/OutboundRequestDetailPage';
import OutboundRequestListPage from './pages/outbound-request/OutboundRequestListPage';
import PickingDetailPage from './pages/picking/PickingDetailPage';
import PickingListPage from './pages/picking/PickingListPage';
import PurchaseOrderDetailPage from './pages/purchase-order/PurchaseOrderDetailPage';
import PurchaseOrderListPage from './pages/purchase-order/PurchaseOrderListPage';
import ShippingDetailPage from './pages/shipping/ShippingDetailPage';
import ShippingListPage from './pages/shipping/ShippingListPage';
import SkuPage from './pages/sku/SkuPage';
import StockPage from './pages/stock/StockPage';
import StorageOccupancyPage from './pages/storage-occupancy/StorageOccupancyPage';
import StoragePage from './pages/storage/StoragePage';
import StorageTransferDetailPage from './pages/storage-transfer/StorageTransferDetailPage';
import StorageTransferListPage from './pages/storage-transfer/StorageTransferListPage';
import StorageTransferNewPage from './pages/storage-transfer/StorageTransferNewPage';
import StockOpnameDetailPage from './pages/stock-opname/StockOpnameDetailPage';
import StockOpnameListPage from './pages/stock-opname/StockOpnameListPage';
import StockOpnameNewPage from './pages/stock-opname/StockOpnameNewPage';
import VendorPage from './pages/vendor/VendorPage';
import { WAREHOUSE_PATHS } from './paths';
import { WarehouseProvider } from './WarehouseContext';

/** Canvas renderer + editor: only loaded when the map is opened. */
const WarehouseMapPage = lazy(() => import('./pages/map/WarehouseMapPage'));

/**
 * WMS feature router, mounted at `/warehouse/*` by the app route registry.
 * `/warehouse` (the v1 tab page) redirects to Stock.
 */
const WarehouseRoutes: React.FC = () => (
  <WarehouseProvider>
    <Routes>
      <Route index element={<Navigate to="stock" replace />} />
      <Route path="stock" element={<StockPage />} />
      <Route path="storage" element={<StoragePage />} />
      <Route path="sku" element={<SkuPage />} />
      <Route path="outbound-request" element={<OutboundRequestListPage />} />
      <Route
        path="outbound-request/:id"
        element={<OutboundRequestDetailPage />}
      />
      <Route path="picking" element={<PickingListPage />} />
      <Route path="picking/:id" element={<PickingDetailPage />} />
      <Route path="shipping" element={<ShippingListPage />} />
      <Route path="shipping/:id" element={<ShippingDetailPage />} />
      <Route path="purchase-order" element={<PurchaseOrderListPage />} />
      <Route path="purchase-order/:id" element={<PurchaseOrderDetailPage />} />
      <Route path="vendor" element={<VendorPage />} />
      <Route path="inbound-request" element={<InboundRequestListPage />} />
      <Route
        path="inbound-request/:id"
        element={<InboundRequestDetailPage />}
      />
      <Route path="goods-receipt" element={<GoodsReceiptListPage />} />
      <Route path="goods-receipt/new" element={<GoodsReceiptNewPage />} />
      <Route path="goods-receipt/:id" element={<GoodsReceiptDetailPage />} />
      {/* transfer storage */}
      <Route path="storage-transfer" element={<StorageTransferListPage />} />
      <Route
        path="storage-transfer/new"
        element={<StorageTransferNewPage />}
      />
      <Route
        path="storage-transfer/:id"
        element={<StorageTransferDetailPage />}
      />
      {/* transfer warehouse (hub → hub) */}
      <Route path="hub-transfer" element={<HubTransferListPage />} />
      <Route path="hub-transfer/new" element={<HubTransferNewPage />} />
      <Route path="hub-transfer/:id" element={<HubTransferDetailPage />} />
      {/* stock opname */}
      <Route path="stock-opname" element={<StockOpnameListPage />} />
      <Route path="stock-opname/new" element={<StockOpnameNewPage />} />
      <Route path="stock-opname/:id" element={<StockOpnameDetailPage />} />
      {/* storage occupancy & map */}
      <Route path="storage-occupancy" element={<StorageOccupancyPage />} />
      <Route
        path="map"
        element={
          <Suspense fallback={null}>
            <WarehouseMapPage />
          </Suspense>
        }
      />
      <Route
        path="*"
        element={<Navigate to={WAREHOUSE_PATHS.stock} replace />}
      />
    </Routes>
  </WarehouseProvider>
);

export default WarehouseRoutes;
